// src/pages/health/components/WorkoutEditorModal.jsx
import { useEffect, useMemo, useRef, useState } from "react";
import { XMarkIcon, ChevronDownIcon } from "@heroicons/react/24/solid";
import {
    useCreateWorkout,
    useUpdateWorkout,
    useDayWorkoutsInvalidate,
} from "../../../../hooks/useWorkouts";

/* ───────── 옵션 ───────── */
const MACHINE_OPTIONS = [
    "레그익스텐션",
    "시티드 레그프레스",
    "시티드 레그컬",
    "시티드 체스트프레스",
    "버터플라이",
    "인클라인 프레스",
    "랫풀다운",
    "롱풀",
    "리어 델트플라이",
    "숄더프레스",
    "암컬",
    "디핑",
    "업도미널",
    "로타리 토르소",
    "이너/아웃싸이",
];
const CARDIO_OPTIONS = ["러닝머신", "자전거", "스텝머신"];

/* ───────── 매핑 ───────── */
function titleToType(t = "") {
    const s = String(t).toLowerCase();
    if (s.includes("러닝") || s.includes("tread")) return "treadmill";
    if (s.includes("자전") || s.includes("cycle") || s.includes("bike")) return "cycle";
    if (s.includes("스텝") || s.includes("step")) return "stepper";
    // 나머지는 머신으로 묶음
    return "machine";
}

/* 어떤 필드를 보일지: */
const FIELD_CONF = {
    machine: { weight: true, sets: true, reps: true },
    treadmill: { speed: true, duration: true, distance: true },
    cycle: { watt: true, duration: true },
    stepper: { watt: true, duration: true },
};

/* ───────── 플랫 드롭다운 ───────── */
function FlatDropdown({ value, options, onChange, className = "" }) {
    const [open, setOpen] = useState(false);
    const ref = useRef(null);
    useEffect(() => {
        const onDocClick = (e) => {
            if (ref.current && !ref.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener("mousedown", onDocClick);
        return () => document.removeEventListener("mousedown", onDocClick);
    }, []);
    return (
        <div className={`relative ${className}`} ref={ref}>
            <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                aria-haspopup="listbox"
                aria-expanded={open}
                className="w-full h-11 rounded-xl border border-gray-200 px-3 bg-gray-50
                   flex items-center justify-between shadow-sm transition active:scale-[0.99]"
            >
                <span className="font-medium truncate">{value}</span>
                <ChevronDownIcon className={`w-5 h-5 text-gray-600 transition-transform ${open ? "rotate-180" : ""}`} />
            </button>

            {open && (
                <ul
                    role="listbox"
                    className="absolute z-30 mt-1 w-full max-h-60 overflow-auto rounded-xl
                     border border-gray-200 bg-white shadow-md"
                >
                    {options.map((opt) => (
                        <li key={opt} role="option" aria-selected={value === opt}>
                            <button
                                type="button"
                                onClick={() => {
                                    onChange(opt);
                                    setOpen(false);
                                }}
                                className={`w-full h-10 px-3 text-left transition ${value === opt
                                    ? "bg-[#E8F1FF] text-[#1F4FD3] font-semibold"
                                    : "hover:bg-gray-100 text-gray-800"
                                    }`}
                            >
                                {opt}
                            </button>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}

/* ───────── 메인 모달 ───────── */
export default function WorkoutEditorModal({ open, mode, date, initial, onClose }) {
    const isEdit = mode === "edit";
    const invalidate = useDayWorkoutsInvalidate();

    /* 카테고리: '웨이트' | '유산소 운동' */
    const [category, setCategory] = useState(
        initial?.kind === "cardio" ? "유산소 운동" : "근력 운동"
    );

    /* 공통/선택된 종목 */
    const [title, setTitle] = useState(initial?.title || "");

    /* 웨이트 전용 */
    const [weightKg, setWeightKg] = useState(initial?.sets?.[0]?.weight_kg ?? "");
    const [sets, setSets] = useState(initial?.sets?.length ?? "");
    const [reps, setReps] = useState(initial?.sets?.[0]?.reps ?? "");

    /* 유산소 공통 */
    const [duration, setDuration] = useState(initial?.duration_min ?? "");

    /* 러닝머신 전용 */
    const [speed, setSpeed] = useState(initial?.cardio?.speed_kph ?? "");
    const [distance, setDistance] = useState(initial?.cardio?.distance_km ?? "");
    const [watt, setWatt] = useState(initial?.cardio?.watt_w ?? "");

    const [notes, setNotes] = useState(initial?.notes ?? "");

    const createMut = useCreateWorkout();
    const updateMut = useUpdateWorkout();

    /* 카테고리 바뀌면 기본 종목 채우기 */
    useEffect(() => {
        if (!open) return;
        const opts = category === "근력 운동" ? MACHINE_OPTIONS : CARDIO_OPTIONS;
        // 분류가 바뀌면 종목을 해당 카테고리의 첫 항목으로 강제 세팅
        setTitle(opts[0]);
    }, [open, category]);

    const options = useMemo(
        () => (category === "근력 운동" ? MACHINE_OPTIONS : CARDIO_OPTIONS),
        [category]
    );
    const exType = useMemo(() => titleToType(title || options[0]), [title, options]);
    const vis = FIELD_CONF[exType];

    const saving = createMut.isPending || updateMut.isPending;

    /* 카테고리/종목 변경 시 필요없는 값 클리어 */
    useEffect(() => {
        if (exType !== "machine") {
            setWeightKg("");
            setSets("");
            setReps("");
        }
        if (exType !== "treadmill") {
            setSpeed("");
            setDistance("");
        }
        if (exType !== "cycle" && exType !== "stepper") {
            setWatt("");
        }
    }, [exType]);


    // ← 파일 상단 훅들 아래, onSave 정의 위 아무 데나 추가
    const prune = (obj) => {
        // 값이 undefined/null/"" 인 키 제거 (1뎁스)
        const out = {};
        Object.entries(obj || {}).forEach(([k, v]) => {
            if (v === undefined || v === null) return;
            if (typeof v === "string" && v.trim() === "") return;
            out[k] = v;
        });
        return out;
    };


    const onSave = async () => {
        const isCardio = category === "유산소 운동";
        const toNum = (v) => (v === "" ? undefined : Number(v));

        // 유산소 세부
        const cardioMetrics = isCardio
            ? (exType === "treadmill"
                ? prune({ speed_kph: toNum(speed), distance_km: toNum(distance) })
                : prune({ watt_w: toNum(watt) }))
            : undefined;

        // 공통 베이스 (update에서도 쓸 바디)
        const payloadBase = prune({
            title,
            notes: notes || undefined,
            duration_min: toNum(duration),
            cardio: isCardio && cardioMetrics && Object.keys(cardioMetrics).length ? cardioMetrics : undefined,
            sets:
                !isCardio && sets
                    ? Array.from({ length: Number(sets) }).map((_, i) =>
                        prune({
                            set_no: i + 1,
                            weight_kg: toNum(weightKg),
                            reps: toNum(reps),
                        })
                    )
                    : undefined,
        });

        try {
            if (isEdit && initial?.id) {
                // ✅ UPDATE: date/kind/category 제외
                await updateMut.mutateAsync({ id: initial.id, data: payloadBase });
            } else {
                // ✅ CREATE: date/kind/category 포함
                const createPayload = prune({
                    ...payloadBase,
                    date,
                    kind: isCardio ? "cardio" : "strength",
                    category: isCardio ? exType : undefined,
                });
                await createMut.mutateAsync(createPayload);
            }

            await invalidate(date);
            onClose();
        } catch (e) {
            console.error(e);
        }
    };


    if (!open) return null;

    return (
        <div className="fixed inset-0 z-[100]">
            <div className="absolute inset-0 bg-black/40" onClick={onClose} />
            <div className="absolute left-1/2 top-1/2 w-[90vw] max-w-md -translate-x-1/2 -translate-y-1/2">
                <div className="rounded-2xl bg-white shadow-lg overflow-hidden">
                    {/* 헤더 */}
                    <div className="flex items-center justify-center h-14 relative">
                        <button
                            onClick={onClose}
                            className="absolute right-2 top-1/2 -translate-y-1/2 p-2 rounded-md hover:bg-gray-100"
                        >
                            <XMarkIcon className="w-7 h-7 text-gray-500" />
                        </button>
                        <div className="font-semibold text-lg md:text-xl">{isEdit ? "운동 수정" : "운동 추가"}</div>
                    </div>

                    <div className="p-4 space-y-4">
                        {/* 분류 */}
                        <div>
                            <div className="mb-1 text-sm font-medium">운동 분류</div>
                            <FlatDropdown
                                value={category}
                                options={["근력 운동", "유산소 운동"]}
                                onChange={setCategory}
                            />
                        </div>

                        {/* 종목 */}
                        <div>
                            <div className="mb-1 text-sm font-medium">상세 운동 종목</div>
                            <FlatDropdown
                                value={title || options[0]}
                                options={options}
                                onChange={setTitle}
                            />
                        </div>

                        {/* ───── 웨이트 입력 ───── */}
                        {exType === "machine" && (
                            <>
                                <div className="grid grid-cols-3 gap-2">
                                    <div>
                                        <div className="mb-1 text-sm font-medium">무게(kg)</div>
                                        <input
                                            className="w-full h-11 rounded-xl border border-gray-200 px-3 bg-gray-50"
                                            value={weightKg}
                                            onChange={(e) => setWeightKg(e.target.value.replace(/[^\d.]/g, ""))}
                                            inputMode="decimal"
                                        />
                                    </div>
                                    <div>
                                        <div className="mb-1 text-sm font-medium">세트</div>
                                        <input
                                            className="w-full h-11 rounded-xl border border-gray-200 px-3 bg-gray-50"
                                            value={sets}
                                            onChange={(e) => setSets(e.target.value.replace(/\D/g, ""))}
                                            inputMode="numeric"
                                        />
                                    </div>
                                    <div>
                                        <div className="mb-1 text-sm font-medium">횟수</div>
                                        <input
                                            className="w-full h-11 rounded-xl border border-gray-200 px-3 bg-gray-50"
                                            value={reps}
                                            onChange={(e) => setReps(e.target.value.replace(/\D/g, ""))}
                                            inputMode="numeric"
                                        />
                                    </div>
                                </div>

                                <div>
                                    <div className="mb-1 text-sm font-medium">운동 시간(분, 선택)</div>
                                    <input
                                        className="w-full h-11 rounded-xl border border-gray-200 px-3 bg-gray-50"
                                        value={duration}
                                        onChange={(e) => setDuration(e.target.value.replace(/\D/g, ""))}
                                        inputMode="numeric"
                                    />
                                </div>
                            </>
                        )}

                        {/* ───── 유산소: 러닝머신 ───── */}
                        {exType === "treadmill" && (
                            <>
                                <div className="grid grid-cols-3 gap-2">
                                    <div>
                                        <div className="mb-1 text-sm font-medium">속도(km/h)</div>
                                        <input
                                            className="w-full h-11 rounded-xl border border-gray-200 px-3 bg-gray-50"
                                            value={speed}
                                            onChange={(e) => setSpeed(e.target.value.replace(/[^\d.]/g, ""))}
                                            inputMode="decimal"
                                        />
                                    </div>
                                    <div>
                                        <div className="mb-1 text-sm font-medium">시간(분)</div>
                                        <input
                                            className="w-full h-11 rounded-xl border border-gray-200 px-3 bg-gray-50"
                                            value={duration}
                                            onChange={(e) => setDuration(e.target.value.replace(/\D/g, ""))}
                                            inputMode="numeric"
                                        />
                                    </div>
                                    <div>
                                        <div className="mb-1 text-sm font-medium">거리(km)</div>
                                        <input
                                            className="w-full h-11 rounded-xl border border-gray-200 px-3 bg-gray-50"
                                            value={distance}
                                            onChange={(e) => setDistance(e.target.value.replace(/[^\d.]/g, ""))}
                                            inputMode="decimal"
                                        />
                                    </div>
                                </div>
                            </>
                        )}

                        {/* ───── 유산소: 자전거/스텝머신 ───── */}
                        {(exType === "cycle" || exType === "stepper") && (
                            <div className="grid grid-cols-2 gap-2">
                                <div>
                                    <div className="mb-1 text-sm font-medium">Watt</div>
                                    <input
                                        className="w-full h-11 rounded-xl border border-gray-200 px-3 bg-gray-50"
                                        value={watt}
                                        onChange={(e) => setWatt(e.target.value.replace(/[^\d.]/g, ""))}
                                        inputMode="numeric"
                                    />
                                </div>
                                <div>
                                    <div className="mb-1 text-sm font-medium">시간(분)</div>
                                    <input
                                        className="w-full h-11 rounded-xl border border-gray-200 px-3 bg-gray-50"
                                        value={duration}
                                        onChange={(e) => setDuration(e.target.value.replace(/\D/g, ""))}
                                        inputMode="numeric"
                                    />
                                </div>
                            </div>
                        )}

                        {/* 메모 */}
                        <div>
                            <div className="mb-1 text-sm font-medium">메모 (선택)</div>
                            <textarea
                                className="w-full min-h-[72px] rounded-xl border border-gray-200 px-3 py-2 bg-gray-50"
                                value={notes}
                                onChange={(e) => setNotes(e.target.value)}
                            />
                        </div>

                        <button
                            onClick={onSave}
                            disabled={saving}
                            className="w-full h-12 rounded-xl bg-[#5F9BFF] text-white disabled:opacity-50 text-lg md:text-xl"
                        >
                            변경 내용 저장
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
